import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuizIcon from '@mui/icons-material/Quiz';
import { lmsService } from '../../services/api';
import { sanitizeCourseHtml } from '../../components/lms/sanitizeHtml';

export default function LmsCourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [myStatus, setMyStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await lmsService.course(id);
      setCourse(res.data);
      setMyStatus(res.data?.my_status || null);
      await lmsService.courseOpen(id).catch(() => {});
      if (!res.data?.my_status) setMyStatus('assigned');
      try {
        const qRes = await lmsService.quizzes({ course: id });
        const list = Array.isArray(qRes.data) ? qRes.data : qRes.data?.results || [];
        setQuizzes(list.filter((q) => q.published));
      } catch {
        setQuizzes([]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка');
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async () => {
    if (acting) return;
    setActing(true);
    try {
      const res = await lmsService.courseComplete(id);
      setMyStatus(res.data?.status || 'done');
    } catch (e) {
      alert(e?.response?.data?.detail || e.message || 'Не вдалося завершити');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lms/courses')}>
          До курсів
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>{error || 'Курс не знайдено'}</Typography>
      </Box>
    );
  }

  const done = myStatus === 'done';

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/lms/courses')} sx={{ mb: 2 }}>
        До курсів
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{course.title}</Typography>
        {done ? (
          <Chip icon={<CheckCircleIcon />} label="Завершено" color="success" size="small" />
        ) : (
          <Chip label="В процесі" color="info" size="small" />
        )}
      </Box>
      {course.description ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>{course.description}</Typography>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} className="lms-rich-root">
        {course.body ? (
          <div
            className="coursehtml"
            dangerouslySetInnerHTML={{
              __html: sanitizeCourseHtml(course.body),
            }}
          />
        ) : (
          <Typography color="text.secondary">Контент курсу ще не додано.</Typography>
        )}
      </Paper>

      {quizzes.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Тест наприкінці курсу</Typography>
          {quizzes.map((q) => (
            <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography sx={{ flex: 1 }}>{q.title}</Typography>
              {q.settings?.pass_mark != null ? (
                <Chip size="small" label={`поріг ${q.settings.pass_mark}%`} variant="outlined" />
              ) : null}
              <Button
                size="small"
                variant="contained"
                startIcon={<QuizIcon />}
                disabled={!q.can_retake && (q.my_attempts || 0) > 0}
                onClick={() => navigate(`/lms/tests/${q.id}`)}
              >
                {(q.my_attempts || 0) > 0 ? 'Пройти ще раз' : 'Пройти тест'}
              </Button>
            </Box>
          ))}
        </Paper>
      ) : null}

      {!done && (
        <Button variant="contained" color="success" onClick={handleComplete} disabled={acting}>
          {acting ? <CircularProgress size={20} /> : 'Позначити як пройдений'}
        </Button>
      )}
    </Box>
  );
}
